import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@hybrid-iptv/ui";
import type { Channel, Program } from "@hybrid-iptv/types";
import { xtreamStreamId } from "@hybrid-iptv/iptv-parser";
import { useChannels, useEpgFor, useProgramIndex, GUIDE_FIRST_HOURS } from "../epg/hooks.js";
import { fetchChannelArchive, useActiveProviderSourceId } from "../epg/api.js";
import { ProviderSetupPrompt, isProviderNotConfigured } from "../components/ProviderSetupPrompt.js";
import { archiveChannels, catchupDayList, catchupProgramsOn, programClock } from "../epg/catchupBrowse.js";
import { playCatchup } from "../epg/catchupAction.js";
import { ProLockedPanel } from "../components/ProLockedPanel.js";
import { useFeatureUnlocked } from "../library/proFeatures.js";

/**
 * Catch-up (Section 16 step 4) — the "virtual DVR". Every other IPTV player puts
 * the archive behind its own tab, because scrolling the guide backwards to find
 * last night's programme is not how anyone looks for it. Pick a channel, pick a
 * day, press play.
 *
 * Listings come from the panel's PER-CHANNEL table, not the guide. The XMLTV
 * feed is trimmed to a few hours back so the grid stays affordable, which leaves
 * it unable to say what was on four days ago; `get_simple_data_table` carries a
 * week of past listings for one channel and costs about 3 KB. The guide index is
 * the fallback for a source that cannot answer it (an M3U playlist has no stream
 * id to ask about), which still gives today.
 */
export function CatchupPage() {
  // Catch-up is Pro (ADR 2026-10-10).
  const unlocked = useFeatureUnlocked("catchup");
  return unlocked ? <CatchupContent /> : <ProLockedPanel feature="catchup" />;
}

function CatchupContent() {
  const sourceId = useActiveProviderSourceId();
  const { data: channelData, isLoading, error } = useChannels(sourceId);
  const channels = useMemo(() => channelData?.channels ?? [], [channelData]);
  const withArchive = useMemo(() => archiveChannels(channels), [channels]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dayStartMs, setDayStartMs] = useState<number | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  const selected: Channel | null = useMemo(
    () => withArchive.find((c) => c.id === selectedId) ?? withArchive[0] ?? null,
    [withArchive, selectedId],
  );
  // The guide for the chosen channel only (Phase 3 step 2a).
  const guideChannels = useMemo(() => (selected ? [selected] : []), [selected]);
  const { data: epgData } = useEpgFor(sourceId, channels, guideChannels, GUIDE_FIRST_HOURS);
  const programIndex = useProgramIndex(epgData, guideChannels);

  // The provider's own id for this channel, read back out of its live URL.
  const streamId = useMemo(() => (selected ? xtreamStreamId(selected.streamUrl) : undefined), [selected]);

  const archive = useQuery({
    queryKey: ["catchup-archive", sourceId, selected?.id ?? ""],
    queryFn: () => fetchChannelArchive(sourceId, streamId!, selected!.id),
    enabled: Boolean(sourceId && streamId && selected),
    // A channel's archive listing changes as slowly as the schedule does, and
    // flicking between channels should not re-ask the panel each time.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  /** The panel's own listing when it answered, else what the guide happens to hold. */
  const programs: Program[] = useMemo(() => {
    if (archive.data?.programs?.length) return archive.data.programs;
    if (!selected || !programIndex) return [];
    return programIndex.getProgramsForChannel(selected.id);
  }, [archive.data, selected, programIndex]);

  const days = useMemo(() => (selected ? catchupDayList(selected, Date.now()) : []), [selected]);

  // Landing on a channel shows its most recent day, and switching channel falls
  // back to "Today" rather than a day the new channel may not keep.
  useEffect(() => {
    const newest = days[0];
    if (newest && !days.some((d) => d.startMs === dayStartMs)) setDayStartMs(newest.startMs);
  }, [days, dayStartMs]);

  const rows = useMemo(() => {
    if (!selected || dayStartMs === null) return [];
    return catchupProgramsOn(selected, programs, dayStartMs, Date.now());
  }, [selected, programs, dayStartMs]);

  if (isProviderNotConfigured(error)) return <ProviderSetupPrompt fullPage />;

  if (isLoading) {
    return (
      <div className="page page--catchup">
        <p className="catchup__muted">Loading channels…</p>
      </div>
    );
  }

  if (withArchive.length === 0) {
    return (
      <div className="page page--catchup">
        <EmptyState
          title="No catch-up on this provider"
          body="None of your channels advertise an archive, so there is nothing to replay. Providers that offer catch-up mark it per channel, and those channels will appear here."
          icon="↺"
        />
      </div>
    );
  }

  async function play(programId: string) {
    const program = rows.find((p) => p.id === programId);
    if (!program || !selected) return;
    setPlaying(programId);
    try {
      await playCatchup(program, selected);
    } finally {
      setPlaying(null);
    }
  }

  return (
    <div className="page page--catchup">
      <header className="catchup__head">
        <h1>Catch-up</h1>
        <p className="catchup__muted">
          {withArchive.length} {withArchive.length === 1 ? "channel keeps" : "channels keep"} an archive you can replay.
        </p>
      </header>

      <div className="catchup__body">
        <aside className="catchup__channels" aria-label="Channels with catch-up">
          {withArchive.map((channel) => (
            <button
              key={channel.id}
              type="button"
              className={`catchup__channel${channel.id === selected?.id ? " catchup__channel--active" : ""}`}
              onClick={() => setSelectedId(channel.id)}
            >
              {channel.logoUrl && <img src={channel.logoUrl} alt="" loading="lazy" />}
              <span className="catchup__channel-name">{channel.name}</span>
              {channel.catchupDays !== undefined && (
                <span className="catchup__channel-days">{channel.catchupDays}d</span>
              )}
            </button>
          ))}
        </aside>

        <section className="catchup__programs">
          <div className="catchup__days" role="tablist" aria-label="Day">
            {days.map((day) => (
              <button
                key={day.startMs}
                type="button"
                role="tab"
                aria-selected={day.startMs === dayStartMs}
                className={`catchup__day${day.startMs === dayStartMs ? " catchup__day--active" : ""}`}
                onClick={() => setDayStartMs(day.startMs)}
              >
                {day.label}
              </button>
            ))}
          </div>

          {archive.isLoading ? (
            <p className="catchup__muted">Loading this channel's archive…</p>
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing to replay on this day"
              body={
                archive.isError
                  ? "Your provider did not return a listing for this channel, so only what the guide still holds can be shown. Try a more recent day."
                  : "Only programmes that have already finished can be replayed. Try an earlier day, or check back after this one ends."
              }
              icon="🕗"
            />
          ) : (
            <ul className="catchup__list">
              {rows.map((program) => (
                <li key={program.id} className="catchup__row">
                  <span className="catchup__time">{programClock(program)}</span>
                  <span className="catchup__title">
                    {program.title}
                    {program.description && <span className="catchup__desc">{program.description}</span>}
                  </span>
                  <button
                    type="button"
                    className="catchup__play"
                    disabled={playing === program.id}
                    onClick={() => void play(program.id)}
                  >
                    {playing === program.id ? "Starting…" : "↺ Watch"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
